import { Outlet, Navigate, useNavigate } from "react-router-dom";
import Breadcrumbs from "./components/Breadcrumbs";
import { useAuth } from "./contexts/AuthContext";
import { useState } from "react";
import { AppBar, Toolbar, IconButton, Box, Menu, MenuItem, Badge, Tooltip } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BugReportIcon from '@mui/icons-material/BugReport';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Suspense } from "react";
import { ThemeSettingsProvider } from "./contexts/ThemeSettingsContext";
import NavBar from "./components/NavBar";
import ThemeToggleButton from "./components/ThemeToggleButton";
import { HelpButton } from "./components/HelpButton";
import { useSurfaceAccess } from "./contexts/SurfaceAccessContext";
import useOpenOperationalMessages from "./hooks/useOpenOperationalMessages";
import { LoadingState } from "./components/common/LoadingStates";

const messageBadgeColor = (severity?: string | null) => {
    if (severity === "critical") return "error" as const;
    if (severity === "warning") return "warning" as const;
    if (severity === "ok") return "success" as const;
    return "info" as const;
};

const Layout = () => {
    const [openMenu, setOpenMenu] = useState<any>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { signOut, isAuthenticated, user } = useAuth();
    const { isSystemUser } = useSurfaceAccess();
    const { count: openMessageCount, worstSeverity } = useOpenOperationalMessages();
    const navigate = useNavigate();

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setOpenMenu(true)
    };

    const handleMenuClose = () => {
        setOpenMenu(false);
    };

    const handleAuthAction = async () => {
        try {
            if (isAuthenticated) {
                await signOut();
            }
            navigate('/login');
        } catch (err: any) {
            console.error('Failed to sign out:', err);
        }
        handleMenuClose();
    };

    const handleEditProfile = () => {
        navigate('/edit-profile');
        handleMenuClose();
    };

    // Format the user's name
    const displayName = isAuthenticated && user?.attributes
        ? `${user.attributes.given_name} ${user.attributes.family_name}`
        : '(not signed in)';

    return (
        <div className="container">
            <div className="top-nav">
                <AppBar position="static" sx={{ boxShadow: 0, flexShrink: 0 }}>
                    <Toolbar
                        sx={{
                            justifyContent: 'space-between',
                            gap: { xs: 0.5, sm: 1.25, md: 2 },
                            minHeight: { xs: 'var(--appbar-height-mobile)', md: 'var(--appbar-height)' },
                            height: { xs: 'var(--appbar-height-mobile)', md: 'var(--appbar-height)' },
                            px: { xs: 1, sm: 2 },
                            py: 0,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                minWidth: 0,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Box sx={{
                                    width: 30, height: 30,
                                    display: 'grid', placeItems: 'center',
                                    flexShrink: 0,
                                    border: '1px solid rgba(155,216,111,0.42)',
                                    borderRadius: '8px',
                                    background: 'radial-gradient(circle at 68% 32%, rgba(155,216,111,0.24), transparent 42%), rgba(255,255,255,0.035)',
                                }}>
                                    <Box sx={{
                                        width: 14, height: 14,
                                        border: '3px solid #9bd86f',
                                        borderLeftColor: '#f4f7f1',
                                        borderRadius: '999px',
                                        transform: 'rotate(-28deg)',
                                    }} />
                                </Box>
                                <Box component="span" sx={{
                                    color: '#ffffff',
                                    fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
                                    fontWeight: 800,
                                    letterSpacing: 0,
                                    lineHeight: 1,
                                    fontFamily: 'var(--gs-font-sans)',
                                }}>
                                    GreenSparks
                                </Box>
                            </Box>
                        </Box >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: { xs: 0.25, sm: 0.5 },
                                flexShrink: 0,
                            }}
                        >
                            <ThemeToggleButton />
                            <HelpButton />
                            {isSystemUser && (
                                <IconButton
                                    color="inherit"
                                    onClick={() => navigate('/theme-info')}
                                    sx={{
                                        minWidth: 'var(--gs-touch-target-min)',
                                        minHeight: 'var(--gs-touch-target-min)',
                                        fontSize: 28,
                                        p: { xs: 0.5, sm: 0.75 },
                                        '&:hover': {
                                            bgcolor: 'transparent',
                                            color: 'rgba(250, 204, 21, 0.96)',
                                        },
                                    }}
                                    aria-label="Open Theme Diagnostics"
                                >
                                    <BugReportIcon />
                                </IconButton>
                            )}
                            <Tooltip title={`${openMessageCount} open operational message${openMessageCount === 1 ? "" : "s"}`}>
                                <IconButton
                                    color="inherit"
                                    onClick={() => navigate('/messages')}
                                    sx={{
                                        minWidth: 'var(--gs-touch-target-min)',
                                        minHeight: 'var(--gs-touch-target-min)',
                                        fontSize: 28,
                                        p: { xs: 0.5, sm: 0.75 },
                                        '&:hover': {
                                            bgcolor: 'transparent',
                                            color: 'rgba(250, 204, 21, 0.96)',
                                        },
                                    }}
                                    aria-label="Open Message Center"
                                >
                                    <Badge
                                        color={messageBadgeColor(worstSeverity)}
                                        badgeContent={openMessageCount}
                                        max={99}
                                        invisible={openMessageCount === 0}
                                    >
                                        <MailOutlineIcon />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                            <IconButton
                                color="inherit"
                                onClick={handleMenuOpen}
                                sx={{
                                    minWidth: 'var(--gs-touch-target-min)',
                                    minHeight: 'var(--gs-touch-target-min)',
                                    fontSize: 28,
                                    p: { xs: 0.5, sm: 0.75 },
                                    '&:hover': {
                                        bgcolor: 'transparent',
                                        color: 'rgba(250, 204, 21, 0.96)',
                                    },
                                }}
                            >
                                <AccountCircleIcon />
                            </IconButton>
                        </Box>
                        <Menu
                            anchorEl={anchorEl}
                            open={openMenu}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            PaperProps={{
                                sx: {
                                    mt: 0.75,
                                },
                            }}
                        >
                            <MenuItem disabled>
                                {displayName}
                            </MenuItem>
                            {isAuthenticated && (
                                <MenuItem onClick={handleEditProfile}>
                                    Edit Profile
                                </MenuItem>
                            )}
                            <MenuItem onClick={handleAuthAction}>
                                {isAuthenticated ? 'Sign Out' : 'Sign In'}
                            </MenuItem>
                        </Menu>
                    </Toolbar>
                </AppBar>
                <NavBar />
                <Breadcrumbs />
            </div>
            <div className="main-content">
                <ThemeSettingsProvider>
                    <main className="content">
                        <Suspense fallback={<LoadingState label="Loading Surface" minHeight="100%" />}>
                            <Outlet />
                        </Suspense>
                    </main>
                </ThemeSettingsProvider>
            </div>
        </div>
    );
};

export default Layout;
